import type { PatientOption } from '../types/chat'

/** Display label from API folder slug (legacy rows may only have slugs in metadata). */
export function formatFolderCandidateSlug(slug: string): string {
  const s = slug.trim()
  if (!s) return '—'
  return s
    .split(/\s+/)
    .map((word) => {
      if (word === '+') return '+'
      if (word.length <= 2) return word.toUpperCase()
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

function optionsFromCandidateSlugs(cands: unknown[]): PatientOption[] {
  return cands.map((raw, i) => {
    const slug = String(raw ?? '').trim()
    return {
      selection_id: String(i),
      label: `${i + 1}. ${formatFolderCandidateSlug(slug)}`,
      folder_slug: slug || undefined,
    }
  })
}

function normalizeOptionRows(raw: unknown): PatientOption[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((o, i) => {
      if (!o || typeof o !== 'object') return null
      const row = o as Record<string, unknown>
      const selection_id =
        typeof row.selection_id === 'string' && row.selection_id.trim()
          ? row.selection_id.trim()
          : String(i)
      const label =
        typeof row.label === 'string' && row.label.trim()
          ? row.label.trim()
          : `${i + 1}. ${formatFolderCandidateSlug(selection_id)}`
      const folder_slug =
        typeof row.folder_slug === 'string' && row.folder_slug.trim()
          ? row.folder_slug.trim()
          : undefined
      return { selection_id, label, folder_slug }
    })
    .filter((o): o is PatientOption => o != null)
}

/** True when the assistant row is clinical-document folder disambiguation (not patient search). */
export function isFolderOptionsAssistantRow(
  md: Record<string, unknown> | null | undefined,
  uiTypeFromRow?: string,
): boolean {
  const ui = (uiTypeFromRow || String(md?.uiType ?? md?.ui_type ?? '')).trim()
  const kind = String(md?.kind ?? '').trim()
  return ui === 'folder_options' || kind === 'folder_clarification'
}

/**
 * Build folder option cards from API row options and/or ``clarification_candidates`` metadata
 * (full list — never truncated to 12).
 */
export function resolveFolderOptionsFromAssistantRow(
  md: Record<string, unknown> | null | undefined,
  optionsFromRow?: unknown,
  uiTypeFromRow?: string,
): PatientOption[] | null {
  if (!isFolderOptionsAssistantRow(md, uiTypeFromRow)) {
    return null
  }
  const fromRow = normalizeOptionRows(optionsFromRow)
  if (fromRow.length > 0) return fromRow

  if (!md || typeof md !== 'object') return null

  const mdOptions = normalizeOptionRows(md.options)
  if (mdOptions.length > 0) return mdOptions

  const kind = String(md.kind ?? '').trim()
  const cands = md.clarification_candidates
  if (kind === 'folder_clarification' && Array.isArray(cands) && cands.length > 0) {
    return optionsFromCandidateSlugs(cands)
  }

  return null
}

/** Short intro only — drop legacy numbered slug lines embedded in message text. */
export function folderOptionsDisplayContent(
  content: string,
  optionCount: number,
  fr: boolean,
): string {
  if (optionCount >= 2) {
    return fr
      ? `${optionCount} documents cliniques correspondent. Touchez une carte ou dites son numéro (ex. 1 ou 2).`
      : `${optionCount} clinical documents match. Tap a card or say its number (e.g. 1 or 2).`
  }
  const lines = content
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
  const first = lines.find((l) => l && !/^\d+\.\s/.test(l)) || ''
  return first || content.trim()
}
